import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { Plus, Trash2, Edit2, Package, Tag, X, Shield, Star, Check, Calendar, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import './PackagesPromos.css';

const PackagesPromos = () => {
    const { packages = [], promotions = [], addPackage, deletePackage, addPromotion, deletePromotion, loading } = useOps();

    // UI State
    const [activeTab, setActiveTab] = useState('packages');
    const [isPkgModalOpen, setIsPkgModalOpen] = useState(false);
    const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);

    // Form State
    const [pkgForm, setPkgForm] = useState({ name: '', price: '', cost_price: '', type: 'standard', duration: '', lessons_count: '' });
    const [promoForm, setPromoForm] = useState({ name: '', code: '', discount_value: '', package_id: '', start_date: '', end_date: '', discount_type: 'flat' });

    const handleAddPackage = async (e) => {
        e.preventDefault();
        await addPackage({
            package_name: pkgForm.name,
            customer_price: parseFloat(pkgForm.price),
            package_cost_price: parseFloat(pkgForm.cost_price || 0),
            lesson_count: parseInt(pkgForm.lessons_count || 0),
            type: pkgForm.type,
            validity_days: pkgForm.duration
        });
        setIsPkgModalOpen(false);
        setPkgForm({ name: '', price: '', cost_price: '', type: 'standard', duration: '', lessons_count: '' });
    };

    const handleAddPromo = async (e) => {
        e.preventDefault();

        // Loss Detection
        const selectedPkg = packages.find(p => p.id === parseInt(promoForm.package_id));
        if (selectedPkg) {
            const discount = parseFloat(promoForm.discount_value);
            const netPrice = promoForm.discount_type === 'flat' ? (selectedPkg.price - discount) : (selectedPkg.price * (1 - discount / 100));

            if (netPrice < selectedPkg.costPrice) {
                if (!window.confirm(`WARNING: This promotion will result in a LOSS-MAKING price ($${netPrice.toFixed(2)} vs Cost $${selectedPkg.costPrice.toFixed(2)}). Do you wish to proceed?`)) {
                    return;
                }
            }
        }

        await addPromotion({
            promotion_name: promoForm.name,
            discount_value: parseFloat(promoForm.discount_value),
            package_id: parseInt(promoForm.package_id),
            discount_type: promoForm.discount_type,
            promotion_start_date: promoForm.start_date,
            promotion_end_date: promoForm.end_date
        });
        setIsPromoModalOpen(false);
        setPromoForm({ name: '', code: '', discount_value: '', package_id: '', start_date: '', end_date: '', discount_type: 'flat' });
    };

    if (loading) return <LoadingSpinner color="indigo-600" message="Loading Catalog..." />;

    return (
        <div className="page-container catalog-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Catalog Management</h2>
                    <p className="page-subtitle">Design your training offers and marketing incentives</p>
                </div>
                <div className="catalog-tabs">
                    <button
                        onClick={() => setActiveTab('packages')}
                        className={`catalog-tab ${activeTab === 'packages' ? 'active' : ''}`}
                    >
                        Packages
                    </button>
                    <button
                        onClick={() => setActiveTab('promotions')}
                        className={`catalog-tab ${activeTab === 'promotions' ? 'active' : ''}`}
                    >
                        Promotions
                    </button>
                </div>
            </div>

            {activeTab === 'packages' ? (
                <div className="fade-in">
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Training Curriculums</h4>
                        <button
                            onClick={() => setIsPkgModalOpen(true)}
                            className="btn btn-primary flex items-center gap-2"
                            style={{ backgroundColor: '#4f46e5' }}
                        >
                            <Plus size={20} /> New Package
                        </button>
                    </div>
                    <div className="package-grid">
                        {packages.map(pkg => (
                            <div key={pkg.id} className="package-card">
                                <span className={`package-type-badge ${pkg.type}`}>
                                    {pkg.type}
                                </span>
                                <div className="package-icon">
                                    <Package size={28} />
                                </div>
                                <h3 className="package-name">{pkg.name}</h3>
                                <p className="package-duration">{pkg.duration} PROGRAM</p>

                                <div className="flex justify-between items-end">
                                    <div className="package-price-wrap">
                                        <span className="package-currency">$</span>
                                        <span className="package-price">{pkg.price}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <button className="icon-btn" title="Edit"><Edit2 size={16} /></button>
                                        <button onClick={() => deletePackage(pkg.id)} className="icon-btn text-red" title="Delete"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="fade-in">
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Active Campaigns</h4>
                        <button
                            onClick={() => setIsPromoModalOpen(true)}
                            className="btn btn-primary flex items-center gap-2"
                            style={{ backgroundColor: '#9333ea' }}
                        >
                            <Plus size={20} /> Launch Promo
                        </button>
                    </div>
                    <div className="promo-grid">
                        {promotions.map(promo => (
                            <div key={promo.id} className="promo-card">
                                <div className="promo-glow" />
                                <div className="flex justify-between items-start">
                                    <span className="promo-code">{promo.code}</span>
                                    <button onClick={() => deletePromotion(promo.id)} className="icon-btn text-slate-400 hover:text-red border-none bg-white/5" title="Archive">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                                <h3 className="promo-name">{promo.name}</h3>
                                <div className="promo-discount-wrap">
                                    <div className="promo-discount">{promo.discountType === 'percentage' ? `${promo.discountValue}%` : `$${promo.discountValue}`} OFF</div>
                                    <div className="promo-linked-pkg text-[10px] font-bold text-slate-400 mt-1 uppercase">
                                        Linked to: {packages.find(p => p.id === promo.packageId)?.name || 'Direct Discount'}
                                    </div>
                                </div>

                                <div className="promo-dates">
                                    <div className="flex items-center gap-1.5">
                                        <Calendar size={12} />
                                        <span>{promo.startDate ? new Date(promo.startDate).toLocaleDateString() : (promo.promotion_start_date ? new Date(promo.promotion_start_date).toLocaleDateString() : 'No start')}</span>
                                    </div>
                                    <ArrowRight size={12} />
                                    <span>{promo.endDate ? new Date(promo.endDate).toLocaleDateString() : (promo.promotion_end_date ? new Date(promo.promotion_end_date).toLocaleDateString() : 'No end')}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            {isPkgModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card max-w-md fade-in">
                        <div className="modal-header border-b pb-4 mb-6">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Create Training Package</h3>
                            <button onClick={() => setIsPkgModalOpen(false)} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-400">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddPackage} className="premium-form">
                            <div className="form-group">
                                <label>PACKAGE IDENTITY</label>
                                <input placeholder="e.g. Bronze Starter" value={pkgForm.name} onChange={e => setPkgForm({ ...pkgForm, name: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>TUITION PRICE (USD)</label>
                                    <input type="number" placeholder="0.00" value={pkgForm.price} onChange={e => setPkgForm({ ...pkgForm, price: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label>CURRICULUM LEVEL</label>
                                    <select value={pkgForm.type} onChange={e => setPkgForm({ ...pkgForm, type: e.target.value })}>
                                        <option value="standard">Standard</option>
                                        <option value="premium">Premium</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>PACKAGE COST (USD)</label>
                                    <input type="number" placeholder="Cost to business" value={pkgForm.cost_price} onChange={e => setPkgForm({ ...pkgForm, cost_price: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label>LESSONS COUNT</label>
                                    <input type="number" placeholder="e.g. 10" value={pkgForm.lessons_count} onChange={e => setPkgForm({ ...pkgForm, lessons_count: e.target.value })} required />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>PROGRAM TIMELINE</label>
                                <input placeholder="e.g. 2 Weeks" value={pkgForm.duration} onChange={e => setPkgForm({ ...pkgForm, duration: e.target.value })} />
                            </div>
                            <div className="flex gap-3 pt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setIsPkgModalOpen(false)}>Discard</button>
                                <button type="submit" className="btn btn-primary flex-1">Save Catalog Entry</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isPromoModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card max-w-md fade-in">
                        <div className="modal-header border-b pb-4 mb-6">
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Launch Marketing Promo</h3>
                            <button onClick={() => setIsPromoModalOpen(false)} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-400">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddPromo} className="premium-form">
                            <div className="form-group">
                                <label>CAMPAIGN NAME</label>
                                <input placeholder="e.g. Summer Blitz" value={promoForm.name} onChange={e => setPromoForm({ ...promoForm, name: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>TARGET PACKAGE</label>
                                    <select value={promoForm.package_id} onChange={e => setPromoForm({ ...promoForm, package_id: e.target.value })} required>
                                        <option value="">Select Package...</option>
                                        {packages.map(p => <option key={p.id} value={p.id}>{p.name} (${p.price})</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>DISCOUNT TYPE</label>
                                    <select value={promoForm.discount_type} onChange={e => setPromoForm({ ...promoForm, discount_type: e.target.value })}>
                                        <option value="flat">Flat Amount ($)</option>
                                        <option value="percentage">Percentage (%)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>DISCOUNT VALUE</label>
                                <input type="number" placeholder="e.g. 15.00" value={promoForm.discount_value} onChange={e => setPromoForm({ ...promoForm, discount_value: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label>START DATE</label>
                                    <input type="date" value={promoForm.start_date} onChange={e => setPromoForm({ ...promoForm, start_date: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label>END DATE</label>
                                    <input type="date" value={promoForm.end_date} onChange={e => setPromoForm({ ...promoForm, end_date: e.target.value })} required />
                                </div>
                            </div>
                            <div className="flex gap-3 pt-6">
                                <button type="button" className="btn btn-secondary flex-1" onClick={() => setIsPromoModalOpen(false)}>Discard</button>
                                <button type="submit" className="btn btn-primary flex-1" style={{ backgroundColor: '#9333ea' }}>Activate Campaign</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PackagesPromos;
